import {Component, OnDestroy, OnInit} from '@angular/core';
import {UserModel} from '../../models/user/user.model';
import {Subject, takeUntil} from 'rxjs';
import {DoctorService} from '../../services/doctor/doctor.service';
import {iResponse, iResponseList} from '../../models/response/response.model';
import {ReservedAppointmentModel} from '../../models/appointment/appointment.model';
import {DatePipe} from '@angular/common';
import {Router, RouterLink} from '@angular/router';
import {DoctorSettingsModel} from '../../models/doctor-settings/doctor-settings.model';

@Component({
  selector: 'app-doctor-dashboard',
    imports: [
        DatePipe,
        RouterLink
    ],
  templateUrl: './doctor-dashboard.component.html',
  styleUrl: './doctor-dashboard.component.sass'
})
export class DoctorDashboardComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>()
    doctor!: UserModel
    settings: DoctorSettingsModel | null = null;

    todayAppointments: ReservedAppointmentModel[] = []
    thisWeekAppointments: ReservedAppointmentModel[] = []

    isLoading = true;

    daysOfWeek = [
        { key: 'Monday', label: 'Montag' },
        { key: 'Tuesday', label: 'Dienstag' },
        { key: 'Wednesday', label: 'Mittwoch' },
        { key: 'Thursday', label: 'Donnerstag' },
        { key: 'Friday', label: 'Freitag' },
        { key: 'Saturday', label: 'Samstag' },
        { key: 'Sunday', label: 'Sonntag' }
    ];

    now = new Date();

    constructor(
        private doctorService: DoctorService,
        private router: Router
    ) {
    }

    ngOnInit(): void {
        this.getMe();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toPatientFile(appointment: ReservedAppointmentModel) {
        this.router.navigate(['patientenakte'], {state:{patient: appointment.patient, appointment: appointment}});
    }

    getOpeningHoursForDay(day: string) {
        if (!this.settings) return [];
        return this.settings.consultingHours.filter(h => h.day === day);
    }

    getHolidaysByMonth() {
        if (!this.settings || !this.settings.holidays) return [];

        const sortedHolidays = [...this.settings.holidays].sort((a, b) =>
            new Date(a.from).getTime() - new Date(b.from).getTime()
        );

        const grouped: { month: string, items: any[] }[] = [];

        sortedHolidays.forEach(holiday => {
            const date = new Date(holiday.from);
            const monthLabel = date.toLocaleString('de-DE', { month: 'long', year: 'numeric' });

            let group = grouped.find(g => g.month === monthLabel);
            if (!group) {
                group = { month: monthLabel, items: [] };
                grouped.push(group);
            }
            group.items.push(holiday);
        });

        return grouped;
    }

    private getMe(): void {
        this.isLoading = true;
        this.doctorService.getMyProfile()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<UserModel>) => {
                    this.doctor = response.data;
                    this.getDoctorSettings();
                    this.getAppointmentsForDoctor();
                },
                error: () => {
                    this.isLoading = false;
                }
            })
    }

    private getDoctorSettings(): void {
        this.doctorService.getDoctorSettings(this.doctor.id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponse<DoctorSettingsModel>) => {
                    this.settings = response.data;
                }
            });
    }

    private getAppointmentsForDoctor(): void {
        const now = new Date();
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(now);
        const day = endOfWeek.getDay();
        const diff = endOfWeek.getDate() - day + (day === 0 ? 0 : 7); // bis zum nächsten Sonntag
        endOfWeek.setDate(diff);
        endOfWeek.setHours(23, 59, 59, 999);

        const query = {
            isConfirmed: true,
            from: startOfToday.toISOString(),
            to: endOfWeek.toISOString()
        }

        this.doctorService.getAppointmentsForDoctor(this.doctor.id, query)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: iResponseList<ReservedAppointmentModel>) => {
                    const allAppointments = response.data;
                    const today = new Date();
                    today.setHours(23, 59, 59, 999);

                    this.todayAppointments = allAppointments.filter(app => new Date(app.from) <= today);
                    this.thisWeekAppointments = allAppointments.filter(app => new Date(app.from) > today);
                    this.isLoading = false;
                },
                error: () => {
                    this.isLoading = false;
                }
            })
    }
}
